import { createAsyncThunk } from "@reduxjs/toolkit";
import NotifyConfig from "../../utils/NotifyConfig";
import ChatService from "../services/chatService";
import {
  isChatSocketOpen,
  sendChatSocketMessage,
} from "../sockets/chat/chatSocketService";

const toMessageRequest = (payload) => ({
  temp_id: payload.temp_id,
  chat_id: payload.chat_id,
  content: payload.content || "",
  type: payload.type || "TEXT",
  file_url: payload.file_url || null,
  file_name: payload.file_name || null,
  file_type: payload.file_type || null,
  file_size: payload.file_size || null,
  duration_ms: payload.duration_ms || null,
  reply_to_id: payload.reply_to_id || null,
});

const createLocalMessageId = () =>
  `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const ChatListThunk = createAsyncThunk("chat/list", async () => {
  try {
    const response = await ChatService.getChats();

    return response;
  } catch (err) {
    NotifyConfig.notifyErrorServer(err.response?.data?.error?.text);
    console.error(err);
    throw err;
  }
});

export const ChatMessagesThunk = createAsyncThunk(
  "chat/messages",
  async ({ chatId, page = 1, pageSize = 50 }) => {
    try {
      const response = await ChatService.getMessagesByChatId(
        chatId,
        page,
        pageSize,
      );

      return {
        chatId,
        response,
      };
    } catch (err) {
      NotifyConfig.notifyErrorServer(err.response?.data?.error?.text);
      console.error(err);
      throw err;
    }
  },
);

export const SendMessageThunk = createAsyncThunk(
  "chat/sendMessage",
  async (payload) => {
    try {
      let nextPayload = payload;
      const localMetadata = {
        temp_id: payload.temp_id || createLocalMessageId(),
        created_at: new Date().toISOString(),
        file_name: payload.file_name || null,
        file_type: payload.file_type || null,
        file_size: payload.file_size || null,
        duration_ms: payload.duration_ms || null,
        reply_preview: payload.reply_preview || null,
        s_username: payload.s_username || "",
        r_username: payload.r_username || "",
        isOptimistic: true,
      };

      if (payload.attachmentFile) {
        const uploadedFile = await ChatService.uploadFile(
          payload.attachmentFile,
        );
        const fileUrl =
          uploadedFile?.url ||
          uploadedFile?.file_url ||
          uploadedFile?.download_url ||
          uploadedFile?.public_url;
        if (!fileUrl || /^(blob:|data:)/i.test(fileUrl)) {
          throw new Error("Upload did not return a file URL");
        }

        nextPayload = {
          ...payload,
          file_url: fileUrl,
        };
      }

      const request = toMessageRequest({
        ...nextPayload,
        temp_id: localMetadata.temp_id,
      });
      let sendResponse = null;

      // The chat page opens the WebSocket asynchronously. A user's first send
      // can happen while it is still CONNECTING; queueing that message left the
      // thunk (and send spinner) pending indefinitely if the handshake stalled.
      // The existing HTTP endpoint is a reliable fallback until the socket is
      // ready, and also covers the small race where an open socket closes just
      // before send().
      if (isChatSocketOpen()) {
        try {
          await sendChatSocketMessage(request);
        } catch {
          sendResponse = await ChatService.sendMessage(request);
        }
      } else {
        sendResponse = await ChatService.sendMessage(request);
      }

      return {
        request,
        localMetadata,
        response: {
          ...request,
          ...(sendResponse && typeof sendResponse === "object"
            ? sendResponse
            : {}),
          ...localMetadata,
        },
      };
    } catch (err) {
      NotifyConfig.notifyErrorServer(
        err.response?.data?.error?.text || err.message,
      );
      console.error(err);
      throw err;
    }
  },
);

export const CreateChatThunk = createAsyncThunk(
  "chat/createChat",
  async ({ friend }) => {
    try {
      const response = await ChatService.createChat(friend?.username);

      return {
        friend,
        response,
      };
    } catch (err) {
      NotifyConfig.notifyErrorServer(err.response?.data?.error?.text);
      console.error(err);
      throw err;
    }
  },
);

export const CreateAiChatThunk = createAsyncThunk(
  "chat/createAiChat",
  async () => {
    try {
      const response = await ChatService.createAiChat();

      return { response };
    } catch (err) {
      NotifyConfig.notifyErrorServer(
        err.response?.data?.error?.text ||
          err.response?.data?.detail ||
          err.message,
      );
      console.error(err);
      throw err;
    }
  },
);

export const CreateGroupChatThunk = createAsyncThunk(
  "chat/createGroupChat",
  async ({ group }) => {
    try {
      const response = await ChatService.createGroupChat({
        name: group.name,
        members: group.memberUsernames,
      });

      return {
        group,
        response,
      };
    } catch (err) {
      NotifyConfig.notifyErrorServer(err.response?.data?.error?.text);
      console.error(err);
      throw err;
    }
  },
);

export const TogglePinChatThunk = createAsyncThunk(
  "chat/togglePin",
  async ({ chatIds, shouldPin }) => {
    try {
      await Promise.all(
        chatIds.map((chatId) =>
          shouldPin
            ? ChatService.pinChat(chatId)
            : ChatService.unpinChat(chatId),
        ),
      );

      return {
        chatIds,
        shouldPin,
      };
    } catch (err) {
      NotifyConfig.notifyErrorServer(err.response?.data?.error?.text);
      console.error(err);
      throw err;
    }
  },
);

export const AiMessageThunk = createAsyncThunk(
  "chat/aiMessage",
  async (payload) => {
    try {
      const response = await ChatService.getAiResponse(payload);

      return response;
    } catch (err) {
      NotifyConfig.notifyErrorServer(err.response?.data?.error?.text);
      console.error(err);
      throw err;
    }
  },
);

export const ToggleMuteChatThunk = createAsyncThunk(
  "chat/toggleMute",
  async ({ chatId, shouldMute }) => {
    try {
      const response = shouldMute
        ? await ChatService.muteChat(chatId)
        : await ChatService.unmuteChat(chatId);

      return {
        chatId,
        shouldMute,
        response,
      };
    } catch (err) {
      NotifyConfig.notifyErrorServer(err.response?.data?.error?.text);
      console.error(err);
      throw err;
    }
  },
);

export const DeleteChatsThunk = createAsyncThunk(
  "chat/deleteChats",
  async ({ chatIds }, { rejectWithValue }) => {
    const uniqueIds = [
      ...new Map(chatIds.map((id) => [String(id), id])).values(),
    ];
    const results = await Promise.allSettled(
      uniqueIds.map((chatId) => ChatService.deleteChat(chatId)),
    );
    const deletedIds = new Set(
      uniqueIds
        .filter((id, index) => results[index].status === "fulfilled")
        .map(String),
    );
    let reconciled = false;
    if (results.some((result) => result.status === "rejected")) {
      // The legacy delete endpoint can commit the deletion and still respond
      // with an error. Reconcile against the authoritative chat list before
      // treating the operation as failed, otherwise the deleted chat remains
      // visible until a manual page refresh.
      try {
        const serverChats = await ChatService.getChats();
        const serverChatIds = new Set(
          serverChats
            .map((chat) => chat?.id ?? chat?.chat_id)
            .filter((chatId) => chatId != null)
            .map(String),
        );
        uniqueIds.forEach((chatId) => {
          if (
            !deletedIds.has(String(chatId)) &&
            !serverChatIds.has(String(chatId))
          ) {
            deletedIds.add(String(chatId));
            reconciled = true;
          }
        });
      } catch {
        // Keep the original delete error when the verification request fails.
      }
    }
    const failedChatIds = uniqueIds.filter((id) => !deletedIds.has(String(id)));
    if (failedChatIds.length) {
      const err =
        results[
          uniqueIds.findIndex((id) => String(id) === String(failedChatIds[0]))
        ].reason;
      NotifyConfig.notifyErrorServer(
        err?.response?.data?.error?.text ||
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Unable to delete chat",
      );
      console.error(err);
    }
    const payload = {
      chatIds: uniqueIds.filter((id) => deletedIds.has(String(id))),
      ...(failedChatIds.length ? { failedChatIds } : {}),
      ...(reconciled ? { reconciled: true } : {}),
    };
    return failedChatIds.length && !payload.chatIds.length
      ? rejectWithValue(payload)
      : payload;
  },
);

export const ChatFriendsThunk = createAsyncThunk("chat/friends", async () => {
  try {
    const response = await ChatService.getChatFriends();

    return response;
  } catch (err) {
    NotifyConfig.notifyErrorServer(err.response?.data?.error?.text);
    console.error(err);
    throw err;
  }
});

export const ChatMembersThunk = createAsyncThunk(
  "chat/members",
  async ({ chatId }) => {
    try {
      const response = await ChatService.getChatMembers(chatId);

      return {
        chatId,
        response,
      };
    } catch (err) {
      NotifyConfig.notifyErrorServer(err.response?.data?.error?.text);
      console.error(err);
      throw err;
    }
  },
);

export const ChatMediaThunk = createAsyncThunk(
  "chat/media",
  async ({ chatId }) => {
    // One panel-open fetch: stats decide which tabs to show, plus the first page of
    // photos/videos, files, and shared posts. Independent requests run in parallel;
    // any single failure degrades to an empty section rather than failing the panel.
    const [stats, media, files, posts] = await Promise.all([
      ChatService.getChatMediaStats(chatId).catch(() => null),
      ChatService.getChatMedia(chatId, { limit: 40 }).catch(() => ({
        media: [],
      })),
      ChatService.getChatMedia(chatId, { type: "FILE", limit: 40 }).catch(
        () => ({
          media: [],
        }),
      ),
      ChatService.getSharedPosts(chatId, { limit: 20 }).catch(() => ({
        posts: [],
      })),
    ]);

    return {
      chatId,
      stats,
      media: Array.isArray(media?.media) ? media.media : [],
      files: Array.isArray(files?.media) ? files.media : [],
      posts: Array.isArray(posts?.posts) ? posts.posts : [],
    };
  },
);

export const SearchMessagesThunk = createAsyncThunk(
  "chat/searchMessages",
  async ({ chatId, query }) => {
    // Let a network failure reject: the reducer keeps the previous state, and the
    // panel keeps showing the instant client-side results for the current query
    // rather than falsely reporting "no matches".
    const response = await ChatService.searchMessages(chatId, query);
    return { chatId, query, response };
  },
);

export const AddGroupMemberThunk = createAsyncThunk(
  "chat/addGroupMember",
  async ({ chatId, username }) => {
    await ChatService.addGroupMember(chatId, username);
    return { chatId, username };
  },
);

export const RemoveGroupMemberThunk = createAsyncThunk(
  "chat/removeGroupMember",
  async ({ chatId, username }) => {
    await ChatService.removeGroupMember(chatId, username);
    return { chatId, username };
  },
);

export const LeaveGroupThunk = createAsyncThunk(
  "chat/leaveGroup",
  async (chatId) => {
    await ChatService.leaveGroup(chatId);
    return { chatId };
  },
);
